import React from 'react';
import { TextField } from '@material-ui/core';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { Event } from '@material-ui/icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';

/**
 * The first of the site's date pickers on react-datepicker, which will replace the Material-UI
 * pickers page by page. Its reason to exist: a calendar that only moves month by month is no way
 * to reach a date years back, so the header carries month and year dropdowns.
 *
 * Dates are plain calendar dates -- `YYYY-MM-DD` in, `YYYY-MM-DD` out, '' for none -- read and
 * written in the browser's own time zone, with no time zone logic of any kind. (react-datepicker
 * can take a `timeZone`; this wrapper deliberately never passes one.)
 */

const useStyles = makeStyles((theme: Theme) => ({
  // One element around everything the picker renders. react-datepicker puts its input and, once
  // open, its popper's container side by side as siblings; in a grid or flex row that extra
  // sibling would take a cell of its own and push the next field out of line.
  root: {
    width: '100%',
    minWidth: 0,
  },
  wrapper: {
    width: '100%',
  },
  popper: {
    zIndex: 1400,
  },
  '@global': {
    '.react-datepicker-popper[data-placement^="bottom"]': {
      paddingTop: theme.spacing(1),
    },
    '.react-datepicker-popper[data-placement^="top"]': {
      paddingBottom: theme.spacing(1),
    },
    '.react-datepicker': {
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.16)',
      overflow: 'hidden',
    },
    '.react-datepicker__triangle': {
      display: 'none',
    },
    '.react-datepicker__header': {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(1.5),
    },
    '.react-datepicker__current-month': {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.subtitle2.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '.react-datepicker__navigation': {
      top: 14,
    },
    '.react-datepicker__navigation-icon::before': {
      borderColor: theme.palette.text.secondary,
      borderWidth: '2px 2px 0 0',
    },
    // The month and year dropdowns, as native selects restyled to sit with the rest of the form.
    '.react-datepicker__month-dropdown-container, .react-datepicker__year-dropdown-container': {
      margin: theme.spacing(0.5, 0.75, 0),
    },
    '.react-datepicker__month-select, .react-datepicker__year-select': {
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 0.75),
      cursor: 'pointer',
    },
    '.react-datepicker__day-name, .react-datepicker__day': {
      width: 34,
      lineHeight: '34px',
      margin: 2,
      color: theme.palette.text.primary,
    },
    '.react-datepicker__day-name': {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '.react-datepicker__month': {
      margin: theme.spacing(1),
    },
    '.react-datepicker__day': {
      borderRadius: 8,
      transition: 'background-color 120ms ease, color 120ms ease',
    },
    '.react-datepicker__day:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '.react-datepicker__day--keyboard-selected': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
    },
    '.react-datepicker__day--selected, .react-datepicker__day--selected:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: theme.typography.fontWeightMedium,
    },
    '.react-datepicker__day--today': {
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.primary.main,
    },
    '.react-datepicker__day--selected.react-datepicker__day--today': {
      color: theme.palette.primary.contrastText,
    },
    '.react-datepicker__day--outside-month': {
      color: theme.palette.text.disabled,
    },
    '.react-datepicker__day--disabled': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
    '.react-datepicker__day--disabled:hover': {
      backgroundColor: 'transparent',
    },
    '.react-datepicker__input-container': {
      display: 'block',
      width: '100%',
    },
  },
}));

const DISPLAY_FORMAT = 'MM/dd/yyyy';

/** react-datepicker hands its props to this; the text may be typed as well as picked. */
const PickerInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { ariaLabel?: string }
>(
  (
    { ariaLabel, value, onChange, onClick, onFocus, onBlur, onKeyDown, placeholder, disabled },
    ref,
  ) => (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      value={value ?? ''}
      onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
      onClick={onClick as React.MouseEventHandler<HTMLDivElement>}
      onFocus={onFocus as React.FocusEventHandler<HTMLInputElement>}
      onBlur={onBlur as React.FocusEventHandler<HTMLInputElement>}
      onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLDivElement>}
      placeholder={placeholder}
      disabled={disabled}
      inputRef={ref}
      inputProps={{ 'aria-label': ariaLabel }}
      InputProps={{ endAdornment: <Event htmlColor="#727272" fontSize="small" /> }}
    />
  ),
);
PickerInput.displayName = 'PickerInput';

interface DatePickerFieldProps {
  /** `YYYY-MM-DD`, or '' for no date. */
  value: string;
  /** The new `YYYY-MM-DD`, or '' when the date is cleared or what was typed is not a date. */
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Bounds of the year dropdown and of what can be picked. Default: 1950 to thirty years on. */
  minDate?: Date;
  maxDate?: Date;
}

const DEFAULT_MIN_DATE = new Date(1950, 0, 1);
const defaultMaxDate = () => new Date(new Date().getFullYear() + 30, 11, 31);

export default function DatePickerField(props: DatePickerFieldProps) {
  const { value, onChange, placeholder, ariaLabel, disabled, minDate, maxDate } = props;
  const classes = useStyles();

  const parsed = value ? dayjs(value) : null;

  return (
    <div className={classes.root}>
      <DatePicker
        selected={parsed && parsed.isValid() ? parsed.toDate() : null}
        onChange={(date: Date | null) =>
          onChange(date && dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : '')
        }
        dateFormat={DISPLAY_FORMAT}
        placeholderText={placeholder}
        disabled={disabled}
        minDate={minDate ?? DEFAULT_MIN_DATE}
        maxDate={maxDate ?? defaultMaxDate()}
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        wrapperClassName={classes.wrapper}
        popperClassName={classes.popper}
        showPopperArrow={false}
        // Fixed, not absolute: Material-UI cards and dialog papers clip what overflows them, which
        // would cut a calendar off at the edge of a short card.
        popperProps={{ strategy: 'fixed' }}
        customInput={<PickerInput ariaLabel={ariaLabel} />}
      />
    </div>
  );
}
